import { zodResolver } from "@hookform/resolvers/zod";
import {
  Slot,
  Stack,
  useGlobalSearchParams,
  useLocalSearchParams,
  useRouter,
  useSegments,
} from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import {
  LocationDisclaimerSheet,
  ReservationPlaceholderWeb,
  ServiceDetailsSheet,
  ServiceFooter,
  loaderRef,
} from "../../../src/components";
import {
  useAppSelector,
  useCheckout,
  useServiceDefaultValues,
  useServiceFormSchemas,
  useServiceNavigation,
} from "../../../src/hooks";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";

import {
  isNative,
  isWeb,
  useDebounce,
  useV2GetBundleBySlugQuery,
  useValidateAddressInLocalizationMutation,
} from "@stepapp/core";
import { Block, palette, spacing, useBreakpoints } from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { any } from "zod";

export type ServiceFormStep = "date-and-address" | "summary";

export const useServiceFormContext = () => useFormContext<ReservationFormParams>();

const PROGRESS_MAX_WIDTH = spacing.unit * 15;

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
export default function ServiceScreen() {
  const { t } = useTranslation();
  const { push, replace } = useRouter();
  const segments = useSegments();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const params = useGlobalSearchParams<{ [key: string]: string }>();
  const { xl } = useBreakpoints();
  const initialSlug = useMemo(() => (slug ? slug : ""), [slug]);
  const { id, properties } = useAppSelector(({ user }) => user);
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const { data, loading: bundleLoading } = useV2GetBundleBySlugQuery({
    variables: { localizationId, slug: initialSlug },
    skip: !(initialSlug && localizationId),
    fetchPolicy: "cache-and-network",
    onError: () => replace("/"),
  });
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);

  const [validateAddress] = useValidateAddressInLocalizationMutation();

  const filteredProperties = useMemo(
    () => properties.filter((item) => item?.localizationId === localizationId),
    [properties, localizationId],
  );
  const hasProperties = useMemo(
    () => !!filteredProperties?.[0]?.propertyId,
    [filteredProperties?.[0]?.propertyId],
  );

  // @ts-expect-error Special case
  const currentSegment = (segments[2] || "") as string;
  const isSummaryScreen = useMemo(() => currentSegment.includes("summary"), [currentSegment]);

  const { platformSegments } = useServiceNavigation(slug);

  const currentSegmentIndex = useMemo(() => {
    const index = platformSegments?.indexOf(currentSegment);

    if (index >= 0) return index;
    if (currentSegment === "payment-method") return platformSegments?.indexOf("summary");

    const pathPart = (params["service-question"] || "") as string;

    return platformSegments?.map((segment) => segment?.split("?")?.[0])?.indexOf(pathPart);
  }, [currentSegment, params, platformSegments]);

  const showProgress = currentSegment !== "confirmation";
  const showPlaceholder =
    xl && isWeb && ["configure", "date-and-address", "summary"].includes(currentSegment);
  const hideFooterOnSegment =
    [
      "",
      "summary",
      "confirmation",
      "date-and-address",
      "configure",
      "payment-method",
      "3ds-authentication-complete",
    ].includes(currentSegment?.toLowerCase()) ||
    (currentSegment === "add-address" && hasProperties);

  const questionId = params?.["service-question"]?.split("_")[1] || "";

  const { checkout, mutateCheckout, loading } = useCheckout();
  const service = useMemo(() => data?.v2GetBundleBySlug, [data]);
  const checkProperty = !!id && filteredProperties?.length > 0;

  const serviceChecklist = useMemo(() => service?.checkList, [service?.checkList]);

  const schemas = useServiceFormSchemas({ service, checkProperty, questionId });

  const defaultValues = useServiceDefaultValues({ bundle: service });

  const methods = useForm<ReservationFormParams>({
    mode: "onChange",
    reValidateMode: "onChange",
    // @ts-expect-error
    resolver: zodResolver(schemas[currentSegment] || any()),
    defaultValues,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (defaultValues?.bundleId) {
      methods.reset(defaultValues);
    }
  }, [defaultValues?.bundleId]);

  const [
    address,
    attributes,
    billingAddress,
    bundleId,
    couponCode,
    houseArea,
    locationId,
    paymentMethodId,
    propertyId,
    services,
    startDate,
    totalPrice,
    detailsShown,
    subscriptionOptionId,
    customerNote,
  ] = methods.watch([
    "address",
    "attributes",
    "billingAddress",
    "bundleId",
    "couponCode",
    "houseArea",
    "locationId",
    "paymentMethodId",
    "propertyId",
    "services",
    "startDate",
    "meta.total",
    "meta.detailsShown",
    "subscriptionOptionId",
    "customerNote",
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const checkoutPayload = useMemo(
    () => ({
      address,
      attributes,
      billingAddress,
      bundleId,
      couponCode,
      houseArea,
      locationId,
      paymentMethodId,
      propertyId,
      services,
      startDate,
      subscriptionOptionId,
      customerNote,
      fingerPrint: "",
    }),
    [
      address,
      address?.notes,
      address?.doorCode,
      address?.streetAddress1,
      address?.streetAddress2,
      address?.postalOrZipCode,
      address?.city,
      address?.floorArea,
      billingAddress?.city,
      billingAddress?.name,
      billingAddress?.line1,
      billingAddress?.line2,
      billingAddress?.postal_code,
      billingAddress?.email,
      billingAddress?.taxIdentification,
      attributes,
      bundleId,
      couponCode,
      houseArea,
      locationId,
      paymentMethodId,
      propertyId,
      services,
      startDate,
      subscriptionOptionId,
      customerNote,
    ],
  );

  const debouncedCheckoutPayload = useDebounce(checkoutPayload, 500, {
    maxWait: 3000,
  });

  useEffect(() => {
    if (
      (data?.v2GetBundleBySlug?.subscriptionOptions &&
        data?.v2GetBundleBySlug?.subscriptionOptions?.length > 0 &&
        debouncedCheckoutPayload.subscriptionOptionId?.length === 0) ||
      !bundleId
    ) {
      return;
    }
    mutateCheckout(debouncedCheckoutPayload, isSummaryScreen);
  }, [
    bundleId,
    mutateCheckout,
    debouncedCheckoutPayload,
    isSummaryScreen,
    data?.v2GetBundleBySlug?.subscriptionOptions,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    methods.setValue("meta.loading", loading);
    if (checkout?.totalPrice) {
      methods.setValue("meta.timeConsumption", checkout?.timeConsumption);
      methods.setValue("meta.total", checkout?.priceAfterDiscounts || 0);
    }
  }, [checkout, loading]);

  const goToNextPage = useCallback(() => {
    const nextSegment = platformSegments?.[currentSegmentIndex + 1];

    push(nextSegment ? `/service/${slug}/${nextSegment}` : "/");
  }, [push, slug, platformSegments, currentSegmentIndex]);

  const checkAddress = useCallback(async () => {
    loaderRef.current?.showLoader();
    const { data } = await validateAddress({
      variables: {
        payload: {
          city: address?.city || "",
          localizationId,
          postalOrZipCode: address?.postalOrZipCode || "",
          streetAddress1: address?.streetAddress1 || "",
          streetAddress2: address?.streetAddress2,
        },
      },
    });

    if (data?.validateAddressInLocalization) {
      loaderRef.current?.hideLoader();
      goToNextPage();
    } else {
      loaderRef.current?.hideLoader();
      return setNotAvailableLocation(true);
    }
  }, [goToNextPage, validateAddress, localizationId, address]);

  const onSubmit = methods.handleSubmit(
    () => {
      if (currentSegment === "add-address") {
        checkAddress();
      } else if (params?.editId) {
        push(`/service/${slug}/summary`);
      } else {
        goToNextPage();
      }
    },
    async (_error) => {
      await methods.trigger();
    },
  );

  const numberOfSteps = currentSegmentIndex / (platformSegments.length - 1);
  const progress = useSharedValue(numberOfSteps > 0 ? PROGRESS_MAX_WIDTH * numberOfSteps : 0);

  const progressAnimatedStyle = useAnimatedStyle(() => ({
    width: progress.value,
  }));

  useEffect(() => {
    if (bundleLoading) {
      progress.value = 0;
      return;
    }
    progress.value = withTiming(
      PROGRESS_MAX_WIDTH * (currentSegmentIndex / (platformSegments.length - 2)),
      {
        duration: 300,
        easing: Easing.inOut(Easing.ease),
      },
    );
  }, [bundleLoading, progress, currentSegmentIndex, platformSegments.length]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleShowDetails = useCallback((showDetails: boolean) => {
    methods.setValue("meta.detailsShown", showDetails);
  }, []);

  // AdRoll snippet
  useEffect(() => {
    if (isNative) return;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.innerHTML = `
            try {
                __adroll.record_user({"adroll_segments": "a3260a5b"});
            } catch (err) {
            }
        `;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const isDisabled = (isWeb ? false : !methods.formState.isValid) || loading;

  return (
    <FormProvider {...methods}>
      <Block flex={1} row={showPlaceholder}>
        <Block flex={1} marginRight={showPlaceholder ? 6 : 0} marginTop={isWeb ? 3 : 1}>
          {showProgress && (
            <Block style={styles.progressBar}>
              <Animated.View style={[styles.progress, progressAnimatedStyle]} />
            </Block>
          )}

          {isWeb ? (
            <Slot initialRouteName="index" />
          ) : (
            <Stack
              initialRouteName="checklist"
              screenOptions={{
                headerShown: false,
                contentStyle: {
                  backgroundColor: palette.common.white,
                  paddingHorizontal: spacing.unit * 2.5,
                },
              }}
            >
              <Stack.Screen
                name="address-autocomplete"
                options={{
                  animation: "slide_from_bottom",
                  presentation: "modal",
                }}
              />
              <Stack.Screen
                name="payment-method"
                options={{
                  animation: "slide_from_bottom",
                  presentation: "modal",
                }}
              />
            </Stack>
          )}
        </Block>

        {showPlaceholder && (
          <ReservationPlaceholderWeb bundleId={bundleId} checklist={serviceChecklist} />
        )}
      </Block>

      {!hideFooterOnSegment && (
        <ServiceFooter
          handleShowDetails={() => handleShowDetails(true)}
          disabled={isDisabled}
          label={t("common.continue")}
          loading={loading}
          onPress={onSubmit}
          price={totalPrice || data?.v2GetBundleBySlug?.startingPrice || ""}
          showDetails={currentSegment !== "checklist"}
        />
      )}

      <ServiceDetailsSheet
        checklist={serviceChecklist}
        hasSelectedOptions={!!totalPrice}
        handleClose={() => handleShowDetails(false)}
        detailsShown={detailsShown}
      />

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  progress: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.primary.accent,
    borderRadius: 5,
  },
  progressBar: {
    marginHorizontal: spacing.unit * (isWeb ? 0 : 2.5),
    backgroundColor: palette.font.placeholder,
    borderRadius: 5,
    height: spacing.unit * 1.5,
    width: spacing.unit * 15,
  },
});
